import { useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { MAX_LENGTH_TEXT, REQUIRED_TEXT } from '@/common/constants';
import { MAX_OPTIONS_COUNT_STATES,useStatesQuery } from '@/modules/core/hooks';
import { CheckDetails } from '@/modules/employer/components/Lockbox/lockbox.types';

enum AddressFields {
  vendorName = 'vendorName',
  line1 = 'line1',
  line2 = 'line2',
  city = 'city',
  state = 'state',
  zipCode = 'zipCode',
}

export interface FormValues {
  [AddressFields.vendorName]: string;
  [AddressFields.line1]: string;
  [AddressFields.line2]: string;
  [AddressFields.city]: string;
  [AddressFields.state]: string;
  [AddressFields.zipCode]: string;
}

const MAX_FIELD_LENGTH = 40;

interface Props {
  checkDetails?: CheckDetails;
}

const useCheckAddressFields = ({ checkDetails }: Props) => {
  const { states } = useStatesQuery();

  const statesCodes = useMemo(() => states?.map((state) => state?.code || ''), [states]);

  const addressFields: Field[] = useMemo(() => [
    {
      name: AddressFields.vendorName,
      type: FieldTypes.Text,
      label: 'Name',
      placeholder: 'Enter name',
      showRequireIcon: true,
      validator: yup
        .string()
        .max(MAX_FIELD_LENGTH, MAX_LENGTH_TEXT(MAX_FIELD_LENGTH))
        .required(REQUIRED_TEXT),
      defaultValue: checkDetails?.vendorName || '',
    },
    {
      name: AddressFields.line1,
      type: FieldTypes.Text,
      label: 'Address line 1',
      placeholder: 'Enter Address line 1',
      showRequireIcon: true,
      validator: yup
        .string()
        .max(MAX_FIELD_LENGTH, MAX_LENGTH_TEXT(MAX_FIELD_LENGTH))
        .required(REQUIRED_TEXT),
      defaultValue: checkDetails?.addressLine1 || '',
    },
    {
      name: AddressFields.line2,
      type: FieldTypes.Text,
      label: 'Address line 2',
      placeholder: 'Enter Address line 2',
      validator: yup
        .string()
        .max(MAX_FIELD_LENGTH, MAX_LENGTH_TEXT(MAX_FIELD_LENGTH)),
      defaultValue: checkDetails?.addressLine2 || '',
    },
    {
      name: AddressFields.city,
      type: FieldTypes.Text,
      label: 'City',
      showRequireIcon: true,
      placeholder: 'Enter City',
      validator: VALIDATORS.CITY,
      defaultValue: checkDetails?.city || '',
    },
    {
      name: AddressFields.state,
      type: FieldTypes.AutocompleteDropdown,
      label: 'State',
      showRequireIcon: true,
      placeholder: 'Select state',
      validator: yup.string().required(REQUIRED_TEXT).oneOf(statesCodes, REQUIRED_TEXT),
      options: states,
      maxOptionsCount: MAX_OPTIONS_COUNT_STATES,
      defaultValue: checkDetails?.state || '',
    },
    {
      name: AddressFields.zipCode,
      type: FieldTypes.ZipCode,
      label: 'ZIP code',
      placeholder: 'Enter ZIP code',
      showRequireIcon: true,
      validator: yup.string().trim().test('len', 'ZIP code isn\'t valid',
        (val) => (val && (val.length === 10 || val.length === 5))
            || (val === '')).required(REQUIRED_TEXT),
      defaultValue: checkDetails?.zip || '',
    },
  ], [states, checkDetails, statesCodes]);

  return {
    addressFields,
  };
};

export default useCheckAddressFields;
